import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, GraduationCapIcon, InfoIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
// Step option lists
const educationLevels = ['High School Diploma / GED', 'Some College', "Associate's Degree", "Bachelor's Degree", "Master's Degree", 'Doctorate / Professional Degree', 'No High School Diploma'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 50; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const startTimeframes = ['Immediately', 'Within 1 month', 'Within 3 months', 'Within 6 months', 'Within 1 year', 'Not sure yet'];
const yesNoOptions = ['Yes', 'No'];
const TOTAL_STEPS = 6;
const RED = '#E25C4F';
const RED_DARK = '#C94A3D';
interface StageInfo {
  label: string;
  steps: number[];
}
const STAGES: StageInfo[] = [{
  label: 'ABOUT YOU',
  steps: [1, 2, 3, 4, 5]
}, {
  label: 'CONTACT INFO',
  steps: [6]
}, {
  label: 'YOUR SCHOOL MATCHES',
  steps: []
}];
function StageProgress({
  currentStep
}: {
  currentStep: number;
}) {
  // Determine fill ratio along the bar
  const totalDots = STAGES.length;
  // Compute fraction: how far we are between stage dots
  let activeStageIdx = 0;
  STAGES.forEach((stage, idx) => {
    if (stage.steps.includes(currentStep)) activeStageIdx = idx;
  });
  // Within active stage, progress through the steps
  const stage = STAGES[activeStageIdx];
  const stepIdx = stage.steps.indexOf(currentStep);
  const stepFraction = stage.steps.length > 1 ? stepIdx / (stage.steps.length - 1) : 1;
  // Translate to overall fraction (0 -> totalDots - 1)
  const overallFraction = (activeStageIdx + stepFraction) / (totalDots - 1);
  const fillPercent = Math.min(100, Math.max(0, overallFraction * 100));
  return <div className="w-full max-w-2xl mx-auto pt-8 pb-2" data-id="element-1780">
      {/* Stage labels */}
      <div className="grid grid-cols-3 mb-3" data-id="element-1781">
        {STAGES.map((s, idx) => <div key={s.label} className={`text-center text-[11px] sm:text-xs font-bold tracking-widest uppercase ${idx === activeStageIdx ? '' : 'text-gray-400'}`} style={idx === activeStageIdx ? {
        color: RED
      } : undefined} data-id="element-1782">
            {s.label}
          </div>)}
      </div>

      {/* Progress bar with dots */}
      <div className="relative" data-id="element-1783">
        {/* Bar background */}
        <div className="absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 bg-gray-200 rounded-full" data-id="element-1784" />
        {/* Bar fill */}
        <motion.div className="absolute top-1/2 left-0 h-1 -translate-y-1/2 rounded-full" style={{
        backgroundColor: RED
      }} initial={false} animate={{
        width: `${fillPercent}%`
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} data-id="element-1785" />
        {/* Dots */}
        <div className="relative flex justify-between items-center" data-id="element-1786">
          {STAGES.map((s, idx) => {
          const isActive = idx <= activeStageIdx;
          return <div key={s.label} className={`w-5 h-5 rounded-full border-2 transition-colors ${isActive ? '' : 'bg-white border-gray-300'}`} style={isActive ? {
            backgroundColor: RED,
            borderColor: RED
          } : undefined} data-id="element-1787" />;
        })}
        </div>
      </div>

      {/* Step counter */}
      <p className="text-center text-sm text-gray-500 mt-4" data-id="element-1788">
        Step {currentStep} of {TOTAL_STEPS}
      </p>
    </div>;
}
interface InfoBannerProps {
  children: React.ReactNode;
}
function InfoBanner({
  children
}: InfoBannerProps) {
  return <div className="mt-5 flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4" data-id="element-1789">
      <div className="flex-shrink-0 mt-0.5 text-blue-600" data-id="element-1790">
        <InfoIcon className="w-4 h-4" data-id="element-1791" />
      </div>
      <p className="text-xs sm:text-sm text-blue-700 leading-relaxed" data-id="element-1792">
        {children}
      </p>
    </div>;
}
export function RequestInformationForm() {
  usePageMeta('Request Information | Find Online Degree Programs', 'Complete a quick form to get matched with accredited online degree programs.');
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state || {}) as {
    degreeInterest?: string;
    areaOfStudy?: string;
    specialization?: string;
  };
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep3
  } = useFormState(prefill);
  const [step, setStep] = useState(1);
  const [zipLoading, setZipLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepError, setStepError] = useState<string>('');
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
        }
      }
    } catch {
      /* ignore */
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const goBack = () => {
    setStepError('');
    if (step === 1) {
      navigate('/request-information');
    } else {
      setStep(step - 1);
    }
  };
  const goNext = () => {
    setStepError('');
    // Per-step validation
    if (step === 1 && !formData.currentEducation) {
      setStepError('Please select your highest level of education.');
      return;
    }
    if (step === 2 && !formData.gradYear) {
      setStepError('Please select your graduation year.');
      return;
    }
    if (step === 3 && !formData.startDate) {
      setStepError('Please select when you would like to start.');
      return;
    }
    if (step === 4 && !formData.isUSCitizen) {
      setStepError('Please select an option.');
      return;
    }
    if (step === 5) {
      if (!formData.address.trim()) {
        setStepError('Please enter your street address.');
        return;
      }
      if (!formData.zipCode.trim() || formData.zipCode.replace(/\D/g, '').length < 5) {
        setStepError('Please enter a valid 5-digit ZIP code.');
        return;
      }
    }
    setStep(step + 1);
  };
  const handleSubmit = async () => {
    setStepError('');
    if (!validateUniStep3()) return;
    setIsSubmitting(true);
    try {
      console.log('Request Information lead submitted:', {
        ...formData,
        phone: stripPhone(formData.phone),
        leadIdToken,
        trustedFormUrl
      });
    } finally {
      setIsSubmitting(false);
    }
    navigate('/thank-you', {
      state: {
        formData: {
          ...formData,
          phone: stripPhone(formData.phone)
        }
      }
    });
  };
  // Card content for each step
  const renderStepCard = () => {
    if (step === 1) {
      return <StepCard title="What is your highest level of education?" onContinue={goNext} ctaLabel="Continue" info="Your education background helps schools understand where you are in your academic journey and recommend the right program level." error={stepError} data-id="element-1793">
          <SelectField value={formData.currentEducation} onChange={v => updateFormData('currentEducation', v)} options={educationLevels.map(l => ({
          label: l,
          value: l
        }))} data-id="element-1794" />
        </StepCard>;
    }
    if (step === 2) {
      return <StepCard title="When did you graduate high school?" onContinue={goNext} ctaLabel="Continue" info="Knowing when you graduated helps us verify eligibility and connect you with programs that fit your timeline." error={stepError} data-id="element-1795">
          <SelectField value={formData.gradYear} onChange={v => updateFormData('gradYear', v)} options={gradYearOptions.map(y => ({
          label: y,
          value: y
        }))} data-id="element-1796" />
        </StepCard>;
    }
    if (step === 3) {
      return <StepCard title="When would you like to start classes?" onContinue={goNext} ctaLabel="Continue" info="Your preferred start date helps us find programs with upcoming enrollment windows that work for your schedule." error={stepError} data-id="element-1797">
          <SelectField value={formData.startDate} onChange={v => updateFormData('startDate', v)} options={startTimeframes.map(t => ({
          label: t,
          value: t
        }))} data-id="element-1798" />
        </StepCard>;
    }
    if (step === 4) {
      return <StepCard title="Are you a U.S. citizen or eligible non-citizen?" onContinue={goNext} ctaLabel="Continue" info="This helps determine your eligibility for federal financial aid and certain programs." error={stepError} data-id="element-1799">
          <SelectField value={formData.isUSCitizen} onChange={v => updateFormData('isUSCitizen', v)} options={yesNoOptions.map(o => ({
          label: o,
          value: o
        }))} data-id="element-1800" />
        </StepCard>;
    }
    if (step === 5) {
      return <StepCard title="Where are you located?" onContinue={goNext} ctaLabel="Continue" info="Your location helps us find programs available in your area and determine eligibility for state-specific financial aid." error={stepError} data-id="element-1801">
          <div className="space-y-5" data-id="element-1802">
            <div data-id="element-1803">
              <label htmlFor="address" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1804">
                Street Address{' '}
                <span style={{
                color: RED
              }} data-id="element-1805">
                  *
                </span>
              </label>
              <input id="address" type="text" value={formData.address} onChange={e => updateFormData('address', e.target.value)} placeholder="123 Main St" className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-0" style={{
              '--tw-ring-color': RED
            } as React.CSSProperties} data-id="element-1806" />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-1807">
              <label htmlFor="zipCode" className="flex items-center gap-2 text-sm font-bold text-gray-800 mb-2" data-id="element-1808">
                <span data-id="element-1809">📍</span>
                Enter your ZIP Code
              </label>
              <div className="relative" data-id="element-1810">
                <input id="zipCode" type="text" value={formData.zipCode} onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                updateFormData('zipCode', val);
                if (val.length === 5) lookupZip(val);
              }} placeholder="e.g. 90210" maxLength={5} className="block w-full px-4 py-3 text-base border-2 rounded-md bg-white focus:outline-none" style={{
                borderColor: RED
              }} data-id="element-1811" />
                {zipLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-1812">
                    <LoaderIcon className="w-4 h-4 animate-spin text-gray-500" data-id="element-1813" />
                  </div>}
              </div>
              {formData.city && formData.state && <p className="text-xs text-gray-600 mt-2" data-id="element-1814">
                  📌 {formData.city}, {formData.state}
                </p>}
            </div>
          </div>
        </StepCard>;
    }
    if (step === 6) {
      const locationLine = formData.city && formData.state ? `${formData.city}, ${formData.state}` : '';
      return <StepCard title="Last step! Where should we send your matches?" subtitle={`We'll match you with top programs in ${locationLine}.`} onContinue={handleSubmit} ctaLabel={isSubmitting ? 'Submitting...' : 'See Matching Colleges'} ctaIcon={<GraduationCapIcon className="w-5 h-5 ml-2 inline-block" data-id="element-1816" />} isSubmitting={isSubmitting} error={stepError} data-id="element-1815">
          <div className="space-y-5" data-id="element-1817">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1818">
              <div data-id="element-1819">
                <label htmlFor="firstName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1820">
                  First Name{' '}
                  <span style={{
                  color: RED
                }} data-id="element-1821">
                    *
                  </span>
                </label>
                <input id="firstName" type="text" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} placeholder="John" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.firstName ? 'border-red-400' : 'border-gray-300'}`} style={{
                '--tw-ring-color': RED
              } as React.CSSProperties} data-id="element-1822" />
                {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1823">
                    {errors.firstName}
                  </p>}
              </div>
              <div data-id="element-1824">
                <label htmlFor="lastName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1825">
                  Last Name{' '}
                  <span style={{
                  color: RED
                }} data-id="element-1826">
                    *
                  </span>
                </label>
                <input id="lastName" type="text" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} placeholder="Doe" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.lastName ? 'border-red-400' : 'border-gray-300'}`} style={{
                '--tw-ring-color': RED
              } as React.CSSProperties} data-id="element-1827" />
                {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1828">{errors.lastName}</p>}
              </div>
            </div>

            <div data-id="element-1829">
              <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1830">
                Email Address{' '}
                <span style={{
                color: RED
              }} data-id="element-1831">
                  *
                </span>
              </label>
              <input id="email" type="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} placeholder="john@example.com" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.email ? 'border-red-400' : 'border-gray-300'}`} style={{
              '--tw-ring-color': RED
            } as React.CSSProperties} data-id="element-1832" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1833">{errors.email}</p>}
            </div>

            <div data-id="element-1834">
              <label htmlFor="phone" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1835">
                Phone Number{' '}
                <span style={{
                color: RED
              }} data-id="element-1836">
                  *
                </span>
              </label>
              <input id="phone" type="tel" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} placeholder="555-123-4567" maxLength={13} className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.phone ? 'border-red-400' : 'border-gray-300'}`} style={{
              '--tw-ring-color': RED
            } as React.CSSProperties} data-id="element-1837" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1838">{errors.phone}</p>}
            </div>

            <label className="flex items-start gap-3 cursor-pointer pt-2" data-id="element-1839">
              <input type="checkbox" checked={formData.tcpaConsent} onChange={e => updateFormData('tcpaConsent', e.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0" style={{
              accentColor: RED
            }} data-id="element-1840" />
              <span className="text-xs text-gray-600 leading-relaxed" data-id="element-1841">
                *I acknowledge that, by clicking the checkbox as my official
                signature, I consent to representatives of OnlineCollegeGuide
                and our Partner Schools contacting me about educational
                opportunities via email, text, or phone, number(s), using an
                automatic dialer. Message and data rates may apply. I understand
                that my consent is not a requirement for enrollment, and I may
                withdraw my consent at any time.
              </span>
            </label>
            {errors.tcpaConsent && <p className="text-xs text-red-600" data-id="element-1842">{errors.tcpaConsent}</p>}
          </div>
        </StepCard>;
    }
    return null;
  };
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-1843">
      <Header data-id="element-1844" />
      <main className="flex-1 py-6 sm:py-10 px-4" data-id="element-1845">
        <div className="max-w-3xl mx-auto" data-id="element-1846">
          {/* Stage progress */}
          <StageProgress currentStep={step} data-id="element-1847" />

          {/* Back button */}
          <div className="mt-2 mb-4" data-id="element-1848">
            <button type="button" onClick={goBack} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors" data-id="element-1849">
              <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-1850" />
              Back
            </button>
          </div>

          {/* Step card */}
          <AnimatePresence mode="wait" data-id="element-1851">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} data-id="element-1852">
              {renderStepCard()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      <Footer data-id="element-1853" />
    </div>;
}
interface StepCardProps {
  title: string;
  subtitle?: string;
  onContinue: () => void;
  ctaLabel: string;
  ctaIcon?: React.ReactNode;
  info?: string;
  error?: string;
  isSubmitting?: boolean;
  children: React.ReactNode;
}
function StepCard({
  title,
  subtitle,
  onContinue,
  ctaLabel,
  ctaIcon,
  info,
  error,
  isSubmitting,
  children
}: StepCardProps) {
  return <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-10" data-id="element-1854">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center leading-tight mb-2" data-id="element-1855">
        {title}
      </h1>
      {subtitle && <p className="text-center text-sm text-gray-500 mb-6" data-id="element-1856">{subtitle}</p>}
      <div className={subtitle ? 'mt-2' : 'mt-6'} data-id="element-1857">{children}</div>

      {error && <p className="mt-3 text-sm text-red-600 text-center" data-id="element-1858">{error}</p>}

      <button type="button" onClick={onContinue} disabled={isSubmitting} className="mt-6 w-full text-white font-bold py-4 px-6 rounded-lg text-base transition-colors duration-200 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed" style={{
      backgroundColor: RED
    }} onMouseEnter={e => {
      if (!isSubmitting) (e.currentTarget as HTMLButtonElement).style.backgroundColor = RED_DARK;
    }} onMouseLeave={e => {
      ;
      (e.currentTarget as HTMLButtonElement).style.backgroundColor = RED;
    }} data-id="element-1859">
        {isSubmitting ? <span className="inline-flex items-center gap-2" data-id="element-1860">
            <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1861" />
            {ctaLabel}
          </span> : <>
            {ctaLabel}
            {ctaIcon ? ctaIcon : <ArrowRightIcon className="w-5 h-5 ml-2 inline-block" data-id="element-1862" />}
          </>}
      </button>

      {info && <InfoBanner data-id="element-1863">{info}</InfoBanner>}
    </div>;
}
interface SelectFieldProps {
  value: string;
  onChange: (v: string) => void;
  options: {
    label: string;
    value: string;
  }[];
}
function SelectField({
  value,
  onChange,
  options
}: SelectFieldProps) {
  return <select value={value} onChange={e => onChange(e.target.value)} className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2" style={{
    '--tw-ring-color': RED,
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0.75rem center',
    backgroundSize: '1.25rem',
    paddingRight: '2.5rem'
  } as React.CSSProperties} data-id="element-1864">
      <option value="" data-id="element-1865">-- Please Select --</option>
      {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-1866">
          {opt.label}
        </option>)}
    </select>;
}